import { useAccessControl, useAuth } from "@airborne/react-admin";
import { Grid } from "@material-ui/core";
import * as React from "react";
import { Datagrid, FunctionField, List } from "react-admin";
import { CustomDateField } from "../../../../Components/CustomDateField";
import CreatedByField from "../../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { SectionTitle } from "../../../../Components/SectionTitle";
import { SourcePbhRequestOrder } from "../Constant/Source";
import { ExcelExport } from "../../../../Helper/ExportExcel";
import { UpdateHistoryExcelColumns, UpdateHistoryExcelName } from "./Constant/UpdateHistoryExcel";
import { SourceVwUser } from "../../../../Constants/Source";
import { formatDate } from "../../../../Helper/MomentHelper";

const PbhRequestUpdateHistory = props => {
  const { canPerform: canPerformInfoHistory } = useAccessControl(SourcePbhRequestOrder, "pbhrequestinfohistory");
  const { user } = useAuth();
  const reqCatList = props?.selectedData?.requirementCategoryList;

  // Function to get formatted delivery date change
  const getFormattedDeliveryDateChange = (isExcel: boolean) => (record: any) => {
    const prevRecord = JSON.parse(record.requestJsonPrev);
    const currentRecord = JSON.parse(record.requestJson);
    const prevDate = formatDate(prevRecord?.RequestedDeliveryDate);
    const currentDate = formatDate(currentRecord?.RequestedDeliveryDate);
    return prevRecord?.RequestedDeliveryDate === currentRecord?.RequestedDeliveryDate
      ? "Not Changed"
      : `${prevDate} ---> ${currentDate}`;
  };

  // Function to get formatted requirement category change
  const getFormattedRequirementCategoryChange = (record: any) => {
    const prevRecord = JSON.parse(record.requestJsonPrev);
    const currentRecord = JSON.parse(record.requestJson);
    const prevCategory = reqCatList?.find(cat => cat.id === prevRecord?.RequirementCategory)?.category || "-";
    const currentCategory = reqCatList?.find(cat => cat.id === currentRecord?.RequirementCategory)?.category || "-";
    return prevRecord?.RequirementCategory === currentRecord?.RequirementCategory
      ? "Not Changed"
      : `${prevCategory} ---> ${currentCategory}`;
  };

  // Function to get formatted value change
  const getFormattedValueChange = (source: string) => (record: any) => {
    const prevRecord = JSON.parse(record.requestJsonPrev);
    const currentRecord = JSON.parse(record.requestJson);
    return prevRecord?.[source] === currentRecord?.[source]
      ? "Not Changed"
      : `${prevRecord?.[source]} ---> ${currentRecord?.[source]}`;
  };

  // Function to get customer remark
  const getCustomerRemark = (record: any) => {
    return record?.customerRemark || "-";
  };

  const renderDefectReasonWithColor = (record: any) => {
    const value = getFormattedValueChange("DefectReason")(record);
    if (!user?.isCustomer && value !== "Not Changed") {
      return (
        <span style={{ backgroundColor: "#c90c0f", color: "white", display: "inline-block", padding: "2px 6px" }}>
          {value}
        </span>
      );
    }
    return value;
  };

  const renderCustomerRemarkWithColor = (record: any) => {
    const value = getCustomerRemark(record);
    if (!user?.isCustomer && value !== "-") {
      return (
        <span style={{ backgroundColor: "#c90c0f", color: "white", display: "inline-block", padding: "2px 6px" }}>
          {value}
        </span>
      );
    }
    return value;
  };

  const ExportToExcel = (records, fetchRelatedRecords) => {
    fetchRelatedRecords(records, "createdBy", SourceVwUser).then(createdBy => {
      const data = records.map(record => ({
        ...record,
        createdBy: /^\d+$/.test(record?.createdBy)
          ? `${createdBy[record.createdBy]?.registerId} - ${createdBy[record.createdBy]?.name}`
          : record?.createdBy,
      }));
      ExcelExport(
        UpdateHistoryExcelColumns,
        data.map(v => [
          getFormattedDeliveryDateChange(true)(v),
          getFormattedRequirementCategoryChange(v),
          getFormattedValueChange("AirportCode")(v),
          getFormattedValueChange("CustomerPo")(v),
          // getFormattedValueChange("CustomerPoLine")(v),
          // getFormattedValueChange("AttentionName")(v),
          // getFormattedValueChange("AttentionPhone")(v),
          getFormattedValueChange("ShippingAddress")(v),
          getFormattedValueChange("ConsigneeName")(v),
          getFormattedValueChange("ConsigneeAddress")(v),
          getFormattedValueChange("DefectReason")(v),
          // getFormattedValueChange("ReleaseType")(v),
          // getFormattedValueChange("Email")(v),
          getCustomerRemark(v),
          v.createdDate,
          v.createdBy,
        ]),
        UpdateHistoryExcelName,
        user,
        {
          cellStyleConfigs: [
            {
              condition: (row, _rowIndex, colIndex) => colIndex === 7 && !user?.isCustomer && row[7] !== "Not Changed",
              style: {
                fill: { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFc90c0f" } },
                font: { color: { argb: "FFFFFFFF" } },
              },
            },
            {
              condition: (row, _rowIndex, colIndex) => colIndex === 8 && !user?.isCustomer && row[8] !== "-",
              style: {
                fill: { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: "FFc90c0f" } },
                font: { color: { argb: "FFFFFFFF" } },
              },
            },
          ],
        }
      );
    });
  };
  return (
    <>
      {canPerformInfoHistory && (
        <Grid style={{ overflow: "auto" }}>
          <SectionTitle align="center" label="Update History" />
          <List
            {...props}
            resource="PbhRequestOrderUpdateHistorys"
            title=" "
            sort={{ field: "createdDate", order: "DESC" }}
            filterDefaultValues={{ requestOrderId__eq: props?.selectedData?.id ?? 0 }}
            perPage={25}
            bulkActionButtons={false}
            exporter={ExportToExcel}
          >
            <Datagrid {...props} optimized>
              <FunctionField label="RDD History" render={getFormattedDeliveryDateChange(false)} />
              <FunctionField label="Requirement Category History" render={getFormattedRequirementCategoryChange} />
              <FunctionField label="Airport Code History" render={getFormattedValueChange("AirportCode")} />
              <FunctionField label="Customer PO" render={getFormattedValueChange("CustomerPo")} />
              {/* <FunctionField label="Customer PO Line" render={getFormattedValueChange("CustomerPoLine")} /> */}
              {/* <FunctionField label="Attention Name History" render={getFormattedValueChange("AttentionName")} />
              <FunctionField label="Attention Phone History" render={getFormattedValueChange("AttentionPhone")} /> */}
              <FunctionField label="Shipping Address History" render={getFormattedValueChange("ShippingAddress")} />
              <FunctionField label="Company/Consignee Name History" render={getFormattedValueChange("ConsigneeName")} />
              <FunctionField
                label="Company/Consignee Address History"
                render={getFormattedValueChange("ConsigneeAddress")}
              />
              <FunctionField label="Defect Reason History" render={renderDefectReasonWithColor} />
              {/* <FunctionField label="Release Type History" render={getFormattedValueChange("ReleaseType")} /> */}
              {/* <FunctionField label="E-Mail History" render={getFormattedValueChange("Email")} /> */}
              <FunctionField label="Remark Of Customer" render={renderCustomerRemarkWithColor} />
              <CustomDateField source="createdDate" label="CREATED DATE" />
              <CreatedByField label="CREATED BY" link={false} />
            </Datagrid>
          </List>
        </Grid>
      )}
    </>
  );
};

export default PbhRequestUpdateHistory;
